import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Cookie, Shield, BarChart3, Settings, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONSENT_KEY = "qn_cookie_consent";

function getStoredConsent() {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = getStoredConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs) => {
    const record = { ...prefs, timestamp: new Date().toISOString(), version: "1.0" };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    setVisible(false);
  };

  const acceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true });
  const rejectNonEssential = () => saveConsent({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => saveConsent(preferences);

  if (!visible) return null;
  // Never overlay full-screen swarm workspaces (/swarm, /csh/swarm, /haven/swarm …)
  // or embedded views — the banner would cover interactive controls.
  const params = new URLSearchParams(location.search || "");
  if (
    location.pathname.includes("/swarm") ||
    location.pathname.startsWith("/embed") ||
    location.pathname.startsWith("/app") ||
    params.get("embed") === "1"
  ) return null;

  const categories = [
    { key: "necessary", icon: Shield, locked: true },
    { key: "analytics", icon: BarChart3, locked: false },
    { key: "marketing", icon: Settings, locked: false },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-[9998] p-4 md:p-6 pointer-events-none"
        data-testid="cookie-consent-banner" data-automation-id="cookie-consent-banner"
      >
        <div className="pointer-events-auto max-w-4xl mx-auto bg-[var(--qn-card)] border border-[var(--qn-border)] rounded-2xl shadow-2xl shadow-black/20 backdrop-blur-sm overflow-hidden">
          {/* Main banner */}
          <div className="p-5 md:p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[var(--qn-primary)]/10 flex items-center justify-center shrink-0">
                <Cookie className="w-5 h-5 text-[var(--qn-primary)]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-[var(--qn-foreground)] mb-1" data-testid="cookie-title" data-automation-id="cookie-title">
                  {t("cookie.title", "We value your privacy")}
                </h3>
                <p className="text-xs text-[var(--qn-foreground-muted)] leading-relaxed mb-4" data-testid="cookie-description" data-automation-id="cookie-description">
                  {t("cookie.description", "We use cookies to enhance your experience, analyze site traffic, and for marketing purposes. By clicking \"Accept All\", you consent to our use of cookies.")}
                  {" "}
                  <Link to="/privacy" className="text-[var(--qn-primary)] hover:underline">{t("cookie.privacyLink", "Privacy Policy")}</Link>
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    onClick={acceptAll}
                    className="btn-primary rounded-full px-5 h-8 text-xs"
                    data-testid="cookie-accept-all" data-automation-id="cookie-accept-all"
                  >
                    {t("cookie.acceptAll", "Accept All")}
                  </Button>
                  <Button
                    onClick={rejectNonEssential}
                    variant="outline"
                    className="rounded-full px-5 h-8 text-xs border-[var(--qn-border)] text-[var(--qn-foreground-muted)]"
                    data-testid="cookie-reject" data-automation-id="cookie-reject"
                  >
                    {t("cookie.rejectNonEssential", "Essential Only")}
                  </Button>
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-xs text-[var(--qn-foreground-subtle)] hover:text-[var(--qn-primary)] transition-colors px-2"
                    data-testid="cookie-customize-toggle" data-automation-id="cookie-customize-toggle"
                  >
                    {showDetails ? t("cookie.hideDetails", "Hide details") : t("cookie.customize", "Customize")}
                  </button>
                </div>
              </div>
              <button
                onClick={rejectNonEssential}
                className="text-[var(--qn-foreground-subtle)] hover:text-[var(--qn-foreground)] transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Expandable details */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="px-5 md:px-6 pb-5 border-t border-[var(--qn-border)] pt-4">
                  <div className="space-y-3">
                    {categories.map(({ key, icon: Icon, locked }) => (
                      <label
                        key={key}
                        className={`flex items-center gap-3 p-3 rounded-lg border border-[var(--qn-border)] ${locked ? "opacity-75" : "cursor-pointer hover:border-[var(--qn-primary)]/30"} transition-colors`}
                        data-testid={`cookie-category-${key}`}
                      >
                        <Icon className="w-4 h-4 text-[var(--qn-foreground-subtle)] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-medium text-[var(--qn-foreground)]">
                            {t(`cookie.cat.${key}`, key.charAt(0).toUpperCase() + key.slice(1))}
                          </span>
                          <p className="text-[10px] text-[var(--qn-foreground-subtle)] leading-relaxed">
                            {t(`cookie.catDesc.${key}`, "")}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={preferences[key]}
                          disabled={locked}
                          onChange={() => !locked && setPreferences(p => ({ ...p, [key]: !p[key] }))}
                          className="w-4 h-4 rounded accent-[var(--qn-primary)]"
                        />
                      </label>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={saveCustom}
                      className="btn-primary rounded-full px-5 h-8 text-xs"
                      data-testid="cookie-save-preferences" data-automation-id="cookie-save-preferences"
                    >
                      {t("cookie.savePreferences", "Save Preferences")}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
